import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { workingHoursEndpoints } from '../../services/api/endpoints/workingHours.endpoint';
import {
  BUSINESS_DAYS,
  DAYS_OF_WEEK,
  DEFAULT_TIME_SLOT,
  WorkingHour,
  WorkingHours,
  WorkingHoursState,
} from '../../types/workingHours';
import { convertTo24HourFormat } from '../../utils/timeUtils';
import { logger } from '../../utils/logger';

// Inital state of working hours
const getInitalOrgWorkingHours = () => {
  const initial: WorkingHours = {};
  DAYS_OF_WEEK.forEach(day => {
    initial[day] = [
      {
        ...DEFAULT_TIME_SLOT,
        enabled: BUSINESS_DAYS.includes(day),
      },
    ];
  });
  return initial;
};

const initialState: WorkingHoursState = {
  items: [],
  orgWorkingHours: [],
  settingsOrgWorkingHours: getInitalOrgWorkingHours(),
  isLoading: false,
  error: null,
};

export const fetchWorkingHours = createAsyncThunk(
  'workingHours/fetchWorkingHours',
  async (args: { organizationId: string }, { rejectWithValue }) => {
    const { organizationId } = args;
    if (!organizationId) return rejectWithValue('Invalid organization ID');

    try {
      const data = await workingHoursEndpoints.getWorkingHours(organizationId);
      logger.info('Working hours fetched successfully');
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch working hours'
      );
    }
  }
);

export const createWorkingHours = createAsyncThunk<
  Partial<WorkingHour>[],
  { organisationId: string; workingHours: Partial<WorkingHour>[] },
  { rejectValue: string }
>(
  'workingHours/createWorkingHours',
  async (
    args: {
      organisationId: string;
      workingHours: Partial<WorkingHour>[];
    },
    { rejectWithValue }
  ) => {
    const { organisationId, workingHours } = args;
    if (!organisationId) return rejectWithValue('Invalid organization or resource ID');

    try {
      const formattedWorkingHours = workingHours.map(wh => ({
        ...wh,
        orgId: organisationId,
      }));

      const results = await workingHoursEndpoints.createMultipleWorkingHours(
        organisationId,
        formattedWorkingHours
      );

      return results;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create working hours'
      );
    }
  }
);

export const updateWorkingHours = createAsyncThunk(
  'workingHours/updateWorkingHours',
  async (
    args: {
      organizationId: string;
      resourceId: number | null;
      workingHours: Partial<WorkingHour>[];
    },
    { rejectWithValue }
  ) => {
    const { organizationId, resourceId, workingHours } = args;
    if (!organizationId || resourceId === null)
      return rejectWithValue('Invalid organization or resource ID');

    try {
      // Delete existing working hours
      await workingHoursEndpoints.deleteAllWorkingHours(
        organizationId,
        workingHours as WorkingHour[]
      );

      // Create new working hours
      const formattedWorkingHours = workingHours.map(wh => ({
        ...wh,
        resourceId,
        orgId: organizationId,
      }));

      const results = await workingHoursEndpoints.createMultipleWorkingHours(
        organizationId,
        formattedWorkingHours
      );

      return results;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update working hours'
      );
    }
  }
);

export const deleteWorkingHour = createAsyncThunk(
  'workingHours/deleteWorkingHour',
  async (args: { organizationId: string; workingHourId: number }, { rejectWithValue }) => {
    const { organizationId, workingHourId } = args;
    if (!organizationId) return rejectWithValue('Invalid organization ID');

    try {
      await workingHoursEndpoints.deleteWorkingHour(organizationId, workingHourId);
      return workingHourId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete working hour'
      );
    }
  }
);

const parseSettingsWorkingHours = (workingHours: Partial<WorkingHour>[]): WorkingHours => {
  const initial: WorkingHours = {};

  // Normalize and group slots by day
  const data = workingHours.map((entry: any) => {
    const day = entry.day.charAt(0).toUpperCase() + entry.day.slice(1); // Capitalize the day
    return {
      id: entry.id,
      day,
      startTime: convertTo24HourFormat(entry.startTime), // Convert to 24-hour format
      endTime: convertTo24HourFormat(entry.endTime),
      enabled: true, // Assuming that fetched slots are enabled
    };
  });

  // Initialize days with an empty array
  DAYS_OF_WEEK.forEach(day => {
    initial[day] = [];
  });

  // Group the time slots by their days
  data.forEach((slot: any) => {
    const day = slot.day;
    if (initial[day]) {
      initial[day].push(slot);
    } else {
      initial[day] = [slot];
    }
  });

  // Fill in any missing days with the default slot
  DAYS_OF_WEEK.forEach(day => {
    if (initial[day].length === 0) {
      initial[day] = [{ ...DEFAULT_TIME_SLOT }];
    }
  });

  return initial;
};

const workingHoursSlice = createSlice({
  name: 'workingHours',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchWorkingHours.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchWorkingHours.fulfilled,
        (state, action: PayloadAction<Partial<WorkingHour>[]>) => {
          state.orgWorkingHours = action.payload;
          state.settingsOrgWorkingHours = parseSettingsWorkingHours(action.payload);
          state.isLoading = false;
        }
      )
      .addCase(fetchWorkingHours.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
        state.isLoading = false;
      })
      .addCase(createWorkingHours.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        createWorkingHours.fulfilled,
        (state, action: PayloadAction<Partial<WorkingHour>[]>) => {
          state.orgWorkingHours = action.payload;
          state.isLoading = false;
        }
      )
      .addCase(createWorkingHours.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
        state.isLoading = false;
      })
      .addCase(updateWorkingHours.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateWorkingHours.fulfilled,
        (state, action: PayloadAction<Partial<WorkingHour>[]>) => {
          state.items = action.payload as WorkingHour[];
          state.isLoading = false;
        }
      )
      .addCase(updateWorkingHours.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
        state.isLoading = false;
      })
      .addCase(deleteWorkingHour.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWorkingHour.fulfilled, (state, action: PayloadAction<number>) => {
        state.orgWorkingHours = state.orgWorkingHours.filter(wh => wh.id !== action.payload);
        state.isLoading = false;
      })
      .addCase(deleteWorkingHour.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
        state.isLoading = false;
      });
  },
});

export const selectWorkingHours = (state: RootState) => state.workingHours;

export default workingHoursSlice.reducer;
